import { useMemo, useState } from 'react';
import { getRandomWords } from './words';

const GRID_SIZE = 5;
const COLOR_CYCLE = ['white', 'brown', 'red', 'blue', 'black'];

function chunkWords(words) {
  const rows = [];
  for (let i = 0; i < words.length; i += GRID_SIZE) {
    rows.push(words.slice(i, i + GRID_SIZE));
  }
  return rows;
}

function shuffleArray(items) {
  const copy = [...items];

  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }

  return copy;
}

function generateCaptainBoard() {
  const starterTeam = Math.random() < 0.5 ? 'red' : 'blue';
  const secondaryTeam = starterTeam === 'red' ? 'blue' : 'red';

  const colors = [
    ...Array(9).fill(starterTeam),
    ...Array(8).fill(secondaryTeam),
    ...Array(7).fill('brown'),
    'black',
  ];

  return {
    starterTeam,
    colors: shuffleArray(colors),
  };
}

export default function App() {
  const [activeSection, setActiveSection] = useState('jugadores');
  const [randomSeed, setRandomSeed] = useState(0);
  const [cellColors, setCellColors] = useState({});
  const [captainBoard, setCaptainBoard] = useState(() => ({
    starterTeam: null,
    colors: Array(GRID_SIZE * GRID_SIZE).fill('white'),
  }));

  const tableRows = useMemo(() => {
    const randomWords = getRandomWords(GRID_SIZE * GRID_SIZE);
    return chunkWords(randomWords);
  }, [randomSeed]);

  const captainRows = useMemo(() => chunkWords(captainBoard.colors), [captainBoard]);

  const handleShuffle = () => {
    setRandomSeed((prev) => prev + 1);
    setCellColors({});
  };

  const handleGenerateCaptainBoard = () => {
    setCaptainBoard(generateCaptainBoard());
  };

  const starterTeamColorClass = captainBoard.starterTeam
    ? `team-${captainBoard.starterTeam}`
    : 'team-neutral';

  const cycleCellColor = (word) => {
    setCellColors((prev) => {
      const currentColor = prev[word] || COLOR_CYCLE[0];
      const currentIndex = COLOR_CYCLE.indexOf(currentColor);
      const nextColor = COLOR_CYCLE[(currentIndex + 1) % COLOR_CYCLE.length];

      return {
        ...prev,
        [word]: nextColor,
      };
    });
  };

  return (
    <div className="app-shell">
      <header className="top-menu">
        <div className="brand">Código Secreto</div>
        <nav aria-label="Menú principal">
          <ul className="menu-options">
            <li>
              <button
                type="button"
                className={`menu-link ${activeSection === 'jugadores' ? 'active' : ''}`}
                onClick={() => setActiveSection('jugadores')}
              >
                Jugadores
              </button>
            </li>
            <li>
              <button
                type="button"
                className={`menu-link ${activeSection === 'capitanes' ? 'active' : ''}`}
                onClick={() => setActiveSection('capitanes')}
              >
                Capitanes
              </button>
            </li>
          </ul>
        </nav>
      </header>

      <main className="content">
        {activeSection === 'jugadores' ? (
          <section className="panel">
            <div className="panel-header">
              <h1>Tabla de palabras (5x5)</h1>
              <button type="button" className="shuffle-button" onClick={handleShuffle}>
                Aleatorizar palabras
              </button>
            </div>

            <table className="word-table" aria-label="Tabla de palabras para jugadores">
              <tbody>
                {tableRows.map((row, rowIndex) => (
                  <tr key={`row-${rowIndex}`}>
                    {row.map((word) => {
                      const colorClass = `word-cell-${cellColors[word] || 'white'}`;
                      return (
                        <td key={word}>
                          <button
                            type="button"
                            className={`word-cell ${colorClass}`}
                            onClick={() => cycleCellColor(word)}
                          >
                            {word}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ) : (
          <section className="panel">
            <div className="panel-header">
              <h1>Capitanes</h1>
              <button
                type="button"
                className={`shuffle-button ${starterTeamColorClass}`}
                onClick={handleGenerateCaptainBoard}
              >
                Generar tablero
              </button>
            </div>

            <p className={`captain-status ${starterTeamColorClass}`}>
              {captainBoard.starterTeam
                ? `Empieza el equipo ${captainBoard.starterTeam === 'red' ? 'rojo' : 'azul'}.`
                : 'Pulsa “Generar tablero” para crear la distribución de colores.'}
            </p>

            <table className="word-table" aria-label="Tabla de capitanes">
              <tbody>
                {captainRows.map((row, rowIndex) => (
                  <tr key={`captain-row-${rowIndex}`}>
                    {row.map((color, colorIndex) => (
                      <td key={`captain-cell-${rowIndex}-${colorIndex}`}>
                        <div className={`captain-cell word-cell-${color}`} aria-hidden="true" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </main>
    </div>
  );
}
